/**
 * Cliente HTTP de Emails (Snov): wrappers tipados sobre `req()` de campana-nueva, como WhatsApp oficial. Nunca se
 * manda `brandId` (el comercio sale del token) y los ids van codificados.
 */

import { req } from "../campana-nueva/api";
import { qs } from "../wa-oficial/api";
import type { Resp } from "../wa-oficial/api";
import type * as T from "./types";

const B = "/activa/emails";
const e = (id: number | string) => encodeURIComponent(String(id));

const qsPersonas = (f: T.FiltrosPersonas = {}) =>
  qs({ campana: f.campana, filtro: f.filtro && f.filtro !== "todas" ? f.filtro : undefined, q: f.q, orden: f.orden && f.orden !== "reciente" ? f.orden : undefined, pagina: f.pagina && f.pagina > 1 ? f.pagina : undefined, porPagina: f.porPagina });

export const emailsApi = {
  estado: (): Promise<Resp<T.EstadoEmails>> => req("GET", `${B}/estado`),
  resumen: (r?: { desde?: string; hasta?: string }): Promise<Resp<T.ResumenEmails>> => req("GET", `${B}/resumen${qs({ desde: r?.desde, hasta: r?.hasta })}`),
  /** El análisis a fondo: del comercio en el rango, o de una campaña (`campana`) con todo lo que pasó con ella. */
  analisis: (r?: { desde?: string; hasta?: string; campana?: number }): Promise<Resp<T.AnalisisEmails>> =>
    req("GET", `${B}/analisis${qs({ desde: r?.desde, hasta: r?.hasta, campana: r?.campana })}`),
  campanas: (): Promise<Resp<T.ListaCampanas>> => req("GET", `${B}/campanas`),
  campana: (id: number | string): Promise<Resp<T.DetalleCampana>> => req("GET", `${B}/campanas/${e(id)}`),
  personas: (f?: T.FiltrosPersonas): Promise<Resp<T.PaginaPersonas>> => req("GET", `${B}/personas${qsPersonas(f)}`),
  exportarPersonas: (f?: Omit<T.FiltrosPersonas, "pagina" | "porPagina">): Promise<Resp<T.ExportacionPersonas>> =>
    req("GET", `${B}/personas/exportar${qsPersonas(f)}`),
  /** La ficha de una persona, por el id de una de sus filas (nunca el mail en la URL). */
  persona: (id: string): Promise<Resp<T.FichaPersona>> => req("GET", `${B}/personas/${e(id)}`),
  respuestas: (f?: { campana?: number; q?: string; tipo?: T.TipoRespuesta; pagina?: number; porPagina?: number }): Promise<Resp<T.PaginaRespuestas>> =>
    req("GET", `${B}/respuestas${qs({ campana: f?.campana, q: f?.q, tipo: f?.tipo, pagina: f?.pagina && f.pagina > 1 ? f.pagina : undefined, porPagina: f?.porPagina })}`),
  // Del equipo
  conexion: (): Promise<Resp<T.Conexion>> => req("GET", `${B}/conexion`),
  /** Responde 202: corre en segundo plano y el avance se lee en `estado()`. */
  sincronizar: (): Promise<Resp<{ enCurso: boolean }>> => req("POST", `${B}/sincronizar`, {}),
  asignarCampana: (id: number, modo: T.ModoAsignacion, brandId?: string): Promise<Resp<T.ResultadoAsignacion>> =>
    req("PUT", `${B}/campanas/${e(id)}/comercio`, modo === "manual" ? { modo, brandId } : { modo }),
  /** `anteriores`: si la casilla ya era de otro comercio, si las campañas que ya mandó se quedan con él (lo seguro) o pasan. */
  asignarRemitente: (id: number, brandId: string | null, anteriores: "quedan" | "pasan" = "quedan"): Promise<Resp<T.ResultadoAsignacion>> =>
    req("PUT", `${B}/remitentes/${e(id)}/comercio`, { brandId, anteriores }),
  /** Quiénes faltan pasar a «No enviar», con lo que escribió cada uno (del comercio, o de una campaña). */
  bajasPendientes: (campana?: number): Promise<Resp<T.BajaPendiente[]>> => req("GET", `${B}/bajas/pendientes${qs({ campana })}`),
  /** ESCRIBE en Snov: pasa a «No enviar» a los elegidos de los que contestaron pidiendo la baja. */
  pasarBajas: (emails: string[], campana?: number): Promise<Resp<T.ResultadoBajas>> =>
    req("POST", `${B}/bajas/no-enviar`, campana ? { campana, emails } : { emails }),
};
